<template>
<div class="geo-item-search land-ai-detections">
  <GeoItemSearchCommon
      v-model:search-params="commonSearchParams"
    :checkbox-key-prefix="checkboxKeyPrefix"
    :search-types="detectionKinds"
    :search-type-map="detectionKindsMap"
    :search-type-default-values="state.detectionKindsDefaultValues"
    :default-days-before="30"
    @change="onChange"
  >
  </GeoItemSearchCommon>
  <div class="contour-area">
    <span class="title-area" @click="state.showContourAreaRange = !state.showContourAreaRange">
      <i class="fas mr4" :class="{
        'fa-caret-right': !state.showContourAreaRange,
        'fa-caret-down': state.showContourAreaRange,
      }"></i>
      閾値<span class="font-sm">{{contourAreaRangeDisp}}</span>
    </span>
    <div class="form-group mb0 mr16 ml15" v-show="state.showContourAreaRange">
      <div class="ws-nowrap">
        <input v-model="state.search.contourAreaFrom" type="text"
               class="ib form-control contour-area-from"
               @change="onChange()">
        <span class="ib waveline ta-center">〜</span>
        <input v-model="state.search.contourAreaTo" type="text"
               class="ib form-control contour-area-to"
               @change="onChange">
      </div>
    </div>
  </div>
</div>
</template>

<script setup lang="ts">
import GeoItemSearchCommon from 'src/components/Top/geoItemSearchComponents/GeoItemSearchCommon.vue';
import type { CommonSearchParams } from 'src/models/geoItemSearch';
import { detectionKindsMap, detectionKinds } from 'src/consts/ext_land_ai_result';
import {
  computed,
  onMounted,
  reactive,
  ref,
} from 'vue';
import type { DetectionsIndexParams } from 'src/models/apis/extLandAiDetections/extLandAiDetectionsRequest';

interface SearchParams {
  contourAreaFrom: number | null;
  contourAreaTo: number | null;
}

interface GeoItemSearchLandAiDetectopmsState {
  detectionKindsDefaultValues: string[];
  showContourAreaRange: boolean;
  search: SearchParams;
}

const emit = defineEmits<{
  (e: 'change'): void;
}>();

const initSearchState = (): SearchParams => {
  return {
    contourAreaFrom: null,
    contourAreaTo: null,
  };
};
const state = reactive<GeoItemSearchLandAiDetectopmsState>({
  detectionKindsDefaultValues: ['pothole'],
  showContourAreaRange: false,
  search: initSearchState(),
});
const setInitialSearchParams = () => {
  const params: SearchParams = {
    contourAreaFrom: 3000,
    contourAreaTo: 10000,
  };
  state.search = params;
};
onMounted(setInitialSearchParams);

// computed
const contourAreaRangeDisp = computed(() => {
  if (!state.search.contourAreaFrom && !state.search.contourAreaTo) return '';

  const ret = `${state.search.contourAreaFrom}〜${state.search.contourAreaTo}`.replace('null', '');
  return `（${ret}）`;
});
const commonSearchParams = ref<CommonSearchParams | null>(null);
// methods
const getSearchParams = (): DetectionsIndexParams | null => {
  if (!commonSearchParams.value) return null;

  const {
    searchTypes: detectionKinds,
    tsFrom: startTs,
    tsTo: endTs,
    ...other
  } = commonSearchParams.value;
  return {
    detection_kinds: detectionKinds as string[],
    start_ts: startTs,
    end_ts: endTs,
    road_names: other.roadNameDisps,
    directions: other.directions,
    kp_calc_from: other.kpCalcFrom,
    kp_calc_to: other.kpCalcTo,
    contour_area_from: state.search.contourAreaFrom,
    contour_area_to: state.search.contourAreaTo,
  };
};
const onChange = () => {
  emit('change');
};

// others
const checkboxKeyPrefix = 'dtcs';

defineExpose({ getSearchParams });
</script>

<style lang="scss" scoped>
.geo-item-search {
  &.land-ai-detections {
    margin: 0 0 0 25px;
  }
}
.contour-area {
  .waveline { width: 20px; }
  .contour-area-from, .contour-area-to { width: 64px; }
}
</style>
